<template>
  <div class="tool-detail-page white-theme pollo-layout">
    <div class="container">
      <section id="object-remover-tool" class="first-screen">
        <div class="breadcrumb">
          <router-link to="/">{{ $t('modelDetail.breadcrumb.home') }}</router-link>
          <span>&rsaquo;</span>
          <router-link to="/tools">{{ $t('tools.page.allTools') }}</router-link>
          <span>&rsaquo;</span>
          <span>{{ toolName }}</span>
        </div>

        <div class="first-screen-grid">
          <div class="action-column">
            <div v-if="!isLoggedIn" class="login-prompt">
              <div class="login-prompt-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <h2>{{ $t('tools.detail.loginRequired') }}</h2>
                <p>{{ $t('tools.detail.loginRequiredDesc') }}</p>
                <button class="btn btn-primary" @click="goLogin">{{ $t('brandSeo.toolFreeTrialCta') }}</button>
              </div>
            </div>

            <div v-else class="tool-interface">
              <div class="upload-control-label">{{ $t("tools['object-remover'].uploadLabel") }}<span>*</span></div>
              <div v-if="processError" class="tool-error--block"><p class="tool-error">{{ processError }}</p></div>
              <InpaintCanvas
                ref="canvas"
                :placeholder-text="$t(&quot;tools['object-remover'].uploadHint&quot;)"
                :max-display-width="canvasMaxDisplayWidth"
                :max-display-height="canvasMaxDisplayHeight"
                @change="onCanvasChange"
              />
              <p v-if="!hasImage" class="upload-spec">{{ $t("tools['object-remover'].uploadSpec") }}</p>
              <div v-if="hasImage && !hasMask" class="inpaint-hint">{{ $t("tools['object-remover'].maskHint") }}</div>
              <div class="tool-options">
                <button class="btn btn-primary btn-lg" :disabled="processing || !hasImage || !hasMask" @click="process">
                  <svg v-if="processing" class="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                  <span>{{ processing ? $t('tools.detail.processing') : $t('brandSeo.toolProcessCta', { action: $t("tools['object-remover'].processButton") }) }}</span>
                </button>
              </div>
            </div>
          </div>

          <div class="showcase-column">
          <div class="tool-hero">
            <div class="tool-hero-info">
              <span class="tool-icon-lg" aria-hidden="true">🧹</span>
              <h1>{{ toolName }}</h1>
              <p class="tool-hero-desc">{{ $t('brandSeo.toolDescription', { description: toolDesc }) }}</p>
            </div>
          </div>

          <div v-if="!processing && !processed" class="demo-section workspace-panel">
            <h2 class="demo-title">{{ $t('tools.detail.demo') }}</h2>
            <div class="demo-comparison">
              <div class="demo-item">
                <span class="demo-label">{{ $t('tools.detail.before') }}</span>
                <div class="demo-image"><img :src="demoBeforeSrc" :alt="$t(&quot;tools['object-remover'].demoBeforeAlt&quot;)" class="clickable-preview" @click.stop="openImage(demoBeforeSrc)"></div>
              </div>
              <div class="demo-arrow"><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg></div>
              <div class="demo-item">
                <span class="demo-label">{{ $t('tools.detail.after') }}</span>
                <div class="demo-image"><img :src="demoAfterSrc" :alt="$t(&quot;tools['object-remover'].demoAfterAlt&quot;)" class="clickable-preview" @click.stop="openImage(demoAfterSrc)"></div>
              </div>
            </div>
          </div>

          <div v-else-if="processing" class="processing-panel workspace-panel">
            <div class="workspace-heading"><h2>{{ $t('tools.detail.processing') }}</h2><span class="processing-status"><i></i>{{ toolName }}</span></div>
            <div class="processing-preview"><img v-if="previewSrc" :src="previewSrc" :alt="$t(&quot;tools['object-remover'].processingAlt&quot;)"><div class="processing-overlay"><div class="loading-spinner"></div></div></div>
            <div class="processing-track"><span></span></div>
          </div>

          <div v-else class="result-panel workspace-panel">
            <div class="workspace-heading"><h2>{{ $t('tools.detail.after') }}</h2><span class="result-status">✓ {{ toolName }}</span></div>
            <div class="output-comparison">
              <div class="output-before"><span class="output-label">{{ $t('tools.detail.before') }}</span><div class="output-image output-image--fill"><img v-if="previewSrc" :src="previewSrc" :alt="$t(&quot;tools['object-remover'].resultBeforeAlt&quot;)" class="compare-img clickable-preview" @click.stop="openImage(previewSrc)"></div></div>
              <div class="output-after"><span class="output-label">{{ $t('tools.detail.after') }}</span><div class="output-image output-image--fill"><img v-if="resultSrc" :src="resultSrc" :alt="$t(&quot;tools['object-remover'].resultAfterAlt&quot;)" class="compare-img clickable-preview" @click.stop="openImage(resultSrc)"></div></div>
            </div>
            <div class="output-actions"><button v-if="resultSrc" type="button" class="btn btn-primary" @click="downloadResult">⬇️ {{ $t('tools.detail.download') }}</button><button class="btn btn-secondary" @click="reset">{{ $t('tools.detail.processNew') }}</button></div>
          </div>
          </div>
        </div>
      </section>

      <div class="seo-content-start detail-section feature-section">
        <div class="section-heading"><span class="section-kicker">{{ $t("tools['object-remover'].featuresKicker") }}</span><h2>{{ $t('brandSeo.sectionTitle', { title: $t("tools['object-remover'].featuresTitle") }) }}</h2><p>{{ $t("tools['object-remover'].featuresIntro") }}</p></div>
        <a v-if="features[0]" class="section-primary-cta" href="#object-remover-tool">{{ features[0].cta }}</a>
        <div class="landing-demo-comparison">
          <div class="landing-demo-half"><img :src="demoBeforeSrc" :alt="$t(&quot;tools['object-remover'].demoBeforeAlt&quot;)" class="clickable-preview" @click.stop="openImage(demoBeforeSrc)"></div>
          <div class="landing-demo-half"><img :src="demoAfterSrc" :alt="$t(&quot;tools['object-remover'].demoAfterAlt&quot;)" class="clickable-preview" @click.stop="openImage(demoAfterSrc)"></div>
          <span class="landing-demo-divider"></span>
        </div>
        <div class="feature-list">
          <article v-for="(feature, idx) in features" :key="'feature-' + idx" class="feature-row">
            <div class="feature-visual"><div class="feature-visual-half"><img :src="featureImages[idx].before" :alt="featureImages[idx].label + ' before'" class="clickable-preview" @click.stop="openImage(featureImages[idx].before)"></div><div class="feature-visual-half"><img :src="featureImages[idx].after" :alt="featureImages[idx].label + ' after'" class="clickable-preview" @click.stop="openImage(featureImages[idx].after)"></div><span class="feature-divider"></span></div>
            <div class="feature-copy"><span class="feature-index">0{{ idx + 1 }}</span><h3>{{ feature.title }}</h3><p>{{ feature.desc }}</p><a class="feature-cta" href="#object-remover-tool">{{ feature.cta }}</a></div>
          </article>
        </div>
      </div>

      <div class="detail-section use-case-section">
        <div class="section-heading"><span class="section-kicker">{{ $t("tools['object-remover'].useCasesKicker") }}</span><h2>{{ $t("tools['object-remover'].useCasesTitle") }}</h2><p>{{ $t("tools['object-remover'].useCasesIntro") }}</p></div>
        <div class="seo-card-grid"><article v-for="(item, idx) in useCases" :key="'case-' + idx" class="seo-card"><h3>{{ item.title }}</h3><p>{{ item.desc }}</p></article></div>
      </div>

      <div class="detail-section how-to-section">
        <div class="section-heading"><span class="section-kicker">{{ $t("tools['object-remover'].howToKicker") }}</span><h2>{{ $t('brandSeo.sectionTitle', { title: $t("tools['object-remover'].howToTitle") }) }}</h2><p>{{ $t("tools['object-remover'].howToIntro") }}</p></div>
        <div class="steps"><div v-for="(step, idx) in steps" :key="idx" class="step"><div class="step-number">{{ idx + 1 }}</div><h3>{{ step.title }}</h3><p>{{ step.desc }}</p></div></div>
      </div>

      <div class="detail-section more-tools-section"><h2>{{ $t('tools.detail.moreTools') }}</h2><div class="grid-5"><ToolListCard v-for="t in otherTools" :key="t.id" :tool-id="t.id" :icon="t.icon" :badge="t.badge" :to="'/tools/' + t.id" /></div></div>

      <div class="detail-section faq-section">
        <div class="section-heading"><h2>{{ $t('brandSeo.faqTitle', { title: $t("tools['object-remover'].faqTitle") }) }}</h2></div>
        <div class="faq-list"><details v-for="(item, idx) in faqs" :key="'faq-' + idx" class="faq-item"><summary>{{ item.q }}</summary><p>{{ item.a }}</p></details></div>
      </div>

      <ToolHowToCta
        :kicker="$t(&quot;tools['object-remover'].howToKicker&quot;)"
        :title="$t('brandSeo.sectionTitle', { title: $t(&quot;tools['object-remover'].howToTitle&quot;) })"
        :intro="$t(&quot;tools['object-remover'].howToIntro&quot;)"
        :steps="steps"
        :cta-label="$t('brandSeo.toolFreeTrialCta')"
        target-id="object-remover-tool"
      />
    </div>

    <div v-if="lightboxSrc" class="image-lightbox" @click="closeImage"><button type="button" class="lightbox-close" :aria-label="$t('common.close')" @click.stop="closeImage">×</button><img :src="lightboxSrc" :alt="toolName"></div>
  </div>
</template>

<script>
import InpaintCanvas from '@/components/InpaintCanvas.vue'
import { submitInpaint, pollInpaint } from '@/api/comfyui'
import { saveOrUpdateAIHistory, formatCreateDate, uploadFile } from '@/api'
import { isAuthenticated } from '@/api/auth'
import ToolListCard from '@/components/ToolListCard.vue'
import ToolHowToCta from '@/components/ToolHowToCta.vue'

/* 当前工具 ID：用于在 tools 列表中排除自身、定位历史记录 */
var TOOL_ID = 'object-remover'
var DEMO_BEFORE_SRC = require('@/assets/images/tool-demos/object-remover-before.webp')
var DEMO_AFTER_SRC = require('@/assets/images/tool-demos/object-remover-after.webp')
var FEATURE_OBJECT_BEFORE_SRC = require('@/assets/images/tool-demos/object-feature-object-before.webp')
var FEATURE_OBJECT_AFTER_SRC = require('@/assets/images/tool-demos/object-feature-object-after.webp')
var FEATURE_PERSON_BEFORE_SRC = require('@/assets/images/tool-demos/object-feature-person-before.webp')
var FEATURE_PERSON_AFTER_SRC = require('@/assets/images/tool-demos/object-feature-person-after.webp')
var FEATURE_DISTRACTION_BEFORE_SRC = require('@/assets/images/tool-demos/object-feature-distraction-before.webp')
var FEATURE_DISTRACTION_AFTER_SRC = require('@/assets/images/tool-demos/object-feature-distraction-after.webp')

export default {
  name: 'ObjectRemoverDetail',
  components: { InpaintCanvas, ToolListCard, ToolHowToCta },
  data() {
    return {
      hasImage: false,        // 是否已上传图片
      hasMask: false,         // 是否已涂抹
      previewSrc: '',         // 原图预览（对比用）
      processing: false,
      processed: false,
      resultSrc: '',          // 移除结果图 URL
      processError: null,
      poller: null,
      lightboxSrc: '',
      viewportWidth: typeof window === 'undefined' ? 1280 : window.innerWidth
    }
  },
  mounted: function() {
    window.addEventListener('resize', this.updateViewportWidth)
  },
  computed: {
    isLoggedIn() {
      return !!this.$store.state.userInfo
    },
    toolName() {
      return this.$t("tools['" + TOOL_ID + "'].name")
    },
    toolDesc() {
      return this.$t("tools['" + TOOL_ID + "'].fullDesc")
    },
    demoBeforeSrc: function() {
      return DEMO_BEFORE_SRC
    },
    demoAfterSrc: function() {
      return DEMO_AFTER_SRC
    },
    canvasMaxDisplayWidth: function() {
      if (this.viewportWidth <= 1100) {
        return Math.max(260, Math.min(680, this.viewportWidth - 112))
      }
      return Math.max(360, Math.min(465, Math.floor((this.viewportWidth - 104) * 0.4)))
    },
    canvasMaxDisplayHeight: function() {
      return this.viewportWidth <= 768 ? 420 : 400
    },
    featureImages: function() {
      return [
        { before: FEATURE_OBJECT_BEFORE_SRC, after: FEATURE_OBJECT_AFTER_SRC, label: this.$t("tools['object-remover'].featureImageLabels.object") },
        { before: FEATURE_PERSON_BEFORE_SRC, after: FEATURE_PERSON_AFTER_SRC, label: this.$t("tools['object-remover'].featureImageLabels.person") },
        { before: FEATURE_DISTRACTION_BEFORE_SRC, after: FEATURE_DISTRACTION_AFTER_SRC, label: this.$t("tools['object-remover'].featureImageLabels.distraction") }
      ]
    },
    features: function() {
      return this.$t("tools['object-remover'].features")
    },
    useCases: function() {
      return this.$t("tools['object-remover'].useCases")
    },
    // 合并工具专属问答与 Miral AI 免费试用说明。
    faqs: function() {
      var toolFaqs = this.$t("tools['object-remover'].faqs")
      var brandFaqs = this.$t('brandSeo.toolFaqs')
      return (Array.isArray(toolFaqs) ? toolFaqs : []).concat(Array.isArray(brandFaqs) ? brandFaqs : [])
    },
    otherTools() {
      return this.$store.state.tools.filter(function(t) { return t.id !== TOOL_ID }).slice(0, 5)
    },
    steps() {
      return this.$t("tools['object-remover'].steps")
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewportWidth)
    if (this.poller) {
      this.poller.stop()
      this.poller = null
    }
  },
  methods: {
    // 同步视口宽度，限制涂抹画布在当前页面可用区域内。
    updateViewportWidth: function() {
      this.viewportWidth = window.innerWidth
    },
    // 打开图片全屏预览。
    openImage: function(src) {
      this.lightboxSrc = src || ''
    },
    // 关闭图片全屏预览。
    closeImage: function() {
      this.lightboxSrc = ''
    },
    goLogin() {
      this.$store.dispatch('showLoginModal')
    },

    /* InpaintCanvas 抛出的 change 事件 */
    onCanvasChange(e) {
      this.hasImage = !!e.imageBlob
      this.hasMask = !!e.hasMask
      /* 同步原图预览（用于结果对比） */
      if (e.imageBlob) {
        var reader = new FileReader()
        var self = this
        reader.onload = function(ev) {
          self.previewSrc = ev.target && ev.target.result ? String(ev.target.result) : ''
        }
        reader.readAsDataURL(e.imageBlob)
      } else {
        this.previewSrc = ''
      }
    },

    /* 核心流程：提交 + 轮询，与图片擦除共用同一套 ComfyUI 接口 */
    process() {
      var self = this
      if (!this.hasImage || !this.hasMask) return
      this.processing = true
      this.processed = false
      this.processError = null

      var canvas = this.$refs.canvas
      var imageBlob = canvas.getImageBlob()
      var maskBlob = null

      var createDate = formatCreateDate(new Date())
      var aiName = this.toolName
      var aiDesc = this.toolName
      var historyId = null
      var originalUrl = ''

      /* 1. 导出 mask blob */
      canvas.getMaskBlob().then(function(blob) {
        maskBlob = blob
        /* 2. 上传原图到服务器（历史记录用） */
        return uploadFile(imageBlob)
      }).then(function(url) {
        originalUrl = url
      }).catch(function() {
        self.processing = false
        self.processError = self.$t('tools.detail.uploadOriginalFailed')
        return Promise.reject('uploadOriginalFailed')
      }).then(function() {
        /* 3. 创建历史记录 */
        return saveOrUpdateAIHistory({
          aiName: aiName,
          taskId: '',
          aiDesc: aiDesc,
          state: 1,
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        })
      }).then(function(hRes) {
        historyId = hRes && (hRes.historyId != null ? hRes.historyId : null)
      }).then(function() {
        /* 4. 提交移除任务（复用 inpaint 擦除接口） */
        return submitInpaint({ imageUrl: originalUrl, maskFile: maskBlob })
      }).then(function(res) {
        /* 5. 轮询结果 */
        self.poller = pollInpaint(res.promptId, function(err, imageUrl) {
          self.poller = null
          if (err) {
            self.onFailed(err, historyId, aiName, aiDesc, originalUrl, createDate)
          } else {
            self.onSuccess(imageUrl, historyId, aiName, aiDesc, originalUrl, createDate)
          }
        })
      }).catch(function(err) {
        if (err === 'uploadOriginalFailed') return
        self.processing = false
        self.processed = false
        self.resultSrc = ''
        self.processError = self.$userFacingError(err, self.$t('common.generateFailedContactSupport'))
      })
    },

    onSuccess(imageUrl, historyId, aiName, aiDesc, originalUrl, createDate) {
      var self = this
      self.processing = false
      self.resultSrc = imageUrl
      self.processed = true
      self.processError = null
      /* 更新历史记录为成功 */
      saveOrUpdateAIHistory({
        aiName: aiName || self.toolName,
        historyId: historyId,
        taskId: '',
        aiDesc: aiDesc || self.toolName,
        state: 2,
        aiResultUrl: imageUrl || '',
        image_large_url: originalUrl || '',
        aiMediaType: 'image',
        createDate: createDate || formatCreateDate(new Date())
      }).catch(function() {})
    },

    onFailed(err, historyId, aiName, aiDesc, originalUrl, createDate) {
      this.processing = false
      this.processed = false
      this.resultSrc = ''
      this.processError = this.$userFacingError(err, this.$t('common.generateFailedContactSupport'))
      saveOrUpdateAIHistory({
        aiName: aiName || this.toolName,
        historyId: historyId,
        taskId: '',
        aiDesc: aiDesc || this.toolName,
        state: 3,
        message: (err && err.message) ? err.message : '',
        aiResultUrl: '',
        image_large_url: originalUrl || '',
        aiMediaType: 'image',
        createDate: createDate || formatCreateDate(new Date())
      }).catch(function() {})
    },

    downloadResult() {
      if (!this.resultSrc) return
      var url = this.resultSrc
      var a = document.createElement('a')
      a.href = url
      a.download = 'object-removed.png'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },

    reset() {
      this.processed = false
      this.resultSrc = ''
      this.processError = null
      if (this.$refs.canvas) this.$refs.canvas.reset()
      this.hasImage = false
      this.hasMask = false
      this.previewSrc = ''
    }
  }
}
</script>

<style scoped>
@import './ModelDetailStyles.css';

.tool-detail-page { padding: 48px 0 80px; }
.breadcrumb { display: flex; align-items: center; gap: 8px; margin-bottom: 32px; font-size: 13px; color: var(--text-muted); }
.breadcrumb a { color: var(--text-secondary); }
.breadcrumb a:hover { color: var(--accent); }
.tool-hero { margin-bottom: 32px; }
.tool-hero-info { text-align: center; max-width: 640px; margin: 0 auto; }
.tool-icon-lg { font-size: 48px; display: block; margin-bottom: 16px; }
.tool-hero-info h1 { font-size: 36px; font-weight: 800; margin-bottom: 12px; }
.tool-hero-desc { font-size: 16px; color: var(--text-secondary); line-height: 1.7; margin-bottom: 24px; }
.login-prompt { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-xl); padding: 24px 28px; text-align: center; margin: 32px auto 0; max-width: 680px; }
.login-prompt-content { max-width: 360px; margin: 0 auto; }
.login-prompt-content svg { display: block; margin: 0 auto; }
.login-prompt h2 { margin: 6px 0 4px; font-size: 20px; }
.login-prompt p { color: var(--text-muted); font-size: 14px; margin-bottom: 12px; }
.tool-interface { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-xl); padding: 32px; margin: 32px auto 0; max-width: 760px; }
.tool-error { color: #f87171; font-size: 13px; margin: 0 0 12px; }
.tool-error--block { text-align: center; margin-bottom: 16px; }
.output-image--fill { padding: 0; overflow: hidden; background: var(--bg-primary); }
.compare-img { width: 100%; height: 100%; object-fit: contain; display: block; min-height: 200px; max-height: 420px; background: var(--bg-primary); }
.tool-options { display: flex; align-items: flex-end; gap: 16px; margin-top: 16px; justify-content: center; }
.tool-result { margin-top: 32px; }
.gen-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; gap: 12px; }
.loading-spinner { width: 40px; height: 40px; border: 3px solid var(--border-color); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.output-comparison { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.output-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; }
.output-image { aspect-ratio: 1; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; }
.output-actions { display: flex; gap: 12px; justify-content: center; margin-top: 12px; }
/* 效果演示对比图 */
.demo-section { margin: 32px auto 0; padding: 24px; background: var(--bg-primary); border-radius: var(--radius-lg); max-width: 760px; }
.demo-title { font-size: 16px; font-weight: 600; text-align: center; margin-bottom: 20px; color: var(--text-primary); }
.demo-comparison { display: flex; align-items: center; justify-content: center; gap: 24px; flex-wrap: wrap; }
.demo-item { flex: 1; min-width: 200px; max-width: 300px; }
.demo-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; text-align: center; }
.demo-image { aspect-ratio: 3/4; border-radius: var(--radius-lg); overflow: hidden; background: var(--bg-secondary); }
.demo-image img { width: 100%; height: 100%; object-fit: cover; }
.demo-arrow { color: var(--text-muted); flex-shrink: 0; }
.inpaint-hint { text-align: center; color: var(--text-muted); font-size: 14px; margin-top: 12px; }
.detail-section { margin-bottom: 64px; }
.detail-section h2 { font-size: 24px; font-weight: 700; margin-bottom: 24px; }
.steps { display: flex; justify-content: center; gap: 48px; flex-wrap: wrap; }
.step { text-align: center; }
.step-number { width: 40px; height: 40px; border-radius: 50%; background: var(--accent); color: #000; display: flex; align-items: center; justify-content: center; font-weight: 700; margin: 0 auto 12px; }
.step h3 { margin-bottom: 6px; }
.step p { font-size: 13px; color: var(--text-muted); }
.spinner { animation: spin 1s linear infinite; }
.tool-detail-page { padding: 0 0 80px; background: #fff; overflow-x: hidden; }
.first-screen { display: flex; flex-direction: column; width: calc(100vw - 72px); min-height: 100vh; box-sizing: border-box; margin-left: calc(50% - 50vw + 36px); padding: 24px 38px 38px; background: radial-gradient(circle at 82% 8%, rgba(45, 212, 191, .12), transparent 30%), #f7f9fc; }
.first-screen .breadcrumb { width: 100%; max-width: 1280px; margin: 0 auto 20px; }
.first-screen .tool-hero { margin-bottom: 26px; }
.first-screen .tool-hero-info { max-width: 820px; }
.first-screen .tool-hero-info h1 { margin: 0 0 12px; color: #111827; font-size: 42px; line-height: 1.15; }
.first-screen .tool-hero-desc { max-width: 760px; margin: 0 auto; color: #65717c; }
.first-screen .tool-icon-lg { margin-bottom: 10px; font-size: 42px; }
.first-screen-grid { display: grid; grid-template-columns: minmax(390px, .88fr) minmax(500px, 1.12fr); align-items: stretch; gap: 32px; width: 100%; max-width: 1180px; margin: 0 auto; flex: 1; }
.action-column { display: flex; min-width: 0; }
.first-screen .tool-interface,
.first-screen .login-prompt { width: 100%; max-width: none; min-height: 600px; box-sizing: border-box; margin: 0; padding: 20px; border: 1px solid #dfe7eb; border-radius: 18px; background: rgba(255, 255, 255, .96); box-shadow: 0 14px 36px rgba(35, 55, 68, .07); }
.first-screen .login-prompt { display: flex; align-items: center; justify-content: center; }
.first-screen .tool-interface { display: flex; flex-direction: column; }
.upload-control-label { margin-bottom: 12px; color: #29323b; font-size: 14px; font-weight: 700; }
.upload-control-label span { margin-left: 3px; color: #e05252; }
.first-screen .tool-interface >>> .inpaint-upload { min-height: 230px; box-sizing: border-box; padding: 36px 20px; border: 1px dashed #8ed3c4; border-radius: 10px; background: #fff; transition: border-color .2s, background .2s; }
.first-screen .tool-interface >>> .inpaint-upload:hover { border-color: #0aa889; background: #f5fffb; }
.first-screen .tool-interface >>> .inpaint-upload-placeholder { color: #8a949d; }
.first-screen .tool-interface >>> .inpaint-upload-placeholder p { margin: 12px 0 0; color: #4f5b66; font-size: 14px; }
.first-screen .tool-interface >>> .inpaint-upload-placeholder span { display: none; }
.upload-spec { min-height: 34px; margin: 10px 0 0; color: #7b8791; font-size: 12px; line-height: 1.55; }
.first-screen .tool-options { width: 100%; margin-top: auto; padding-top: 18px; }
.first-screen .tool-options .btn { width: 100%; justify-content: center; }
.first-screen .btn-lg { min-width: 210px; }
.inpaint-hint { color: #66737e; }
.workspace-panel { width: 100%; min-height: 440px; box-sizing: border-box; margin: 0; padding: 18px; border: 1px solid #e2e8ec; border-radius: 18px; background: #fff; box-shadow: 0 16px 40px rgba(35, 55, 68, .08); }
.first-screen .workspace-panel { min-height: 600px; }
.workspace-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.workspace-heading h2 { margin: 0; color: #202731; font-size: 18px; }
.processing-status,
.result-status { display: inline-flex; align-items: center; gap: 7px; color: #07856f; font-size: 12px; font-weight: 700; }
.processing-status i { width: 7px; height: 7px; border-radius: 50%; background: #0aa889; box-shadow: 0 0 0 5px rgba(10, 168, 137, .12); }
.processing-preview { position: relative; height: 400px; overflow: hidden; border-radius: 14px; background: #edf1f3; }
.processing-preview img { width: 100%; height: 100%; object-fit: contain; }
.processing-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(247, 250, 251, .55); }
.processing-track { height: 5px; overflow: hidden; margin-top: 16px; border-radius: 99px; background: #e8eeef; }
.processing-track span { display: block; width: 42%; height: 100%; border-radius: inherit; background: #0aa889; animation: processing-slide 1.4s ease-in-out infinite alternate; }
@keyframes processing-slide { to { transform: translateX(138%); } }
.workspace-panel .demo-title { display: block; margin: 0 0 18px; color: #202731; }
.workspace-panel .demo-comparison { height: 392px; min-height: 0; gap: 0; flex-wrap: nowrap; }
.workspace-panel .demo-item { min-width: 0; }
.workspace-panel .demo-image { aspect-ratio: 3 / 4; }
.workspace-panel .demo-arrow { width: 28px; }
.workspace-panel .output-comparison { gap: 12px; }
.result-panel { display: flex; flex-direction: column; }
.result-panel .output-comparison { flex: 1; }
.result-panel .output-before,
.result-panel .output-after { display: flex; min-width: 0; flex-direction: column; }
.workspace-panel .output-image { min-height: 0; flex: 1; aspect-ratio: auto; background: #edf1f3; }
.workspace-panel .compare-img { max-height: none; }
.clickable-preview { cursor: zoom-in; }
.seo-content-start { margin-top: 90px; }
.detail-section { max-width: 1180px; margin: 0 auto 92px; }
.section-heading { max-width: 780px; margin: 0 auto 42px; text-align: center; }
.section-heading h2 { margin: 8px 0 12px; color: #15202b; font-size: 34px; line-height: 1.22; }
.section-heading p { margin: 0; color: #697681; line-height: 1.75; }
.section-kicker { color: #07856f; font-size: 12px; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; }
.feature-list { display: grid; gap: 82px; }
.feature-row { display: grid; grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr); align-items: center; gap: 72px; }
.feature-row:nth-child(even) .feature-visual { order: 2; }
.feature-row:nth-child(even) .feature-copy { order: 1; }
.feature-visual { position: relative; display: grid; grid-template-columns: 1fr 1fr; min-height: 360px; overflow: hidden; border-radius: 22px; background: #eef2f3; box-shadow: 0 20px 45px rgba(33, 53, 65, .1); }
.feature-visual-half { min-width: 0; overflow: hidden; }
.feature-visual img { width: 100%; height: 100%; object-fit: cover; }
.feature-divider { position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; background: rgba(255, 255, 255, .9); }
.feature-copy { padding: 18px 0; }
.feature-index { color: #0aa889; font-size: 13px; font-weight: 800; letter-spacing: .12em; }
.feature-copy h3 { margin: 12px 0; color: #17212b; font-size: 27px; line-height: 1.3; }
.feature-copy p { color: #697681; line-height: 1.75; }
.feature-cta { display: inline-flex; align-items: center; justify-content: center; min-height: 46px; margin-top: 12px; padding: 0 24px; border: 1px solid #0a9c82; border-radius: 999px; color: #087d68; font-size: 14px; font-weight: 750; transition: color .2s, background .2s; }
.feature-cta:hover { color: #fff; background: #0a9c82; }
.seo-card-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.seo-card { padding: 26px; border: 1px solid #e4eaed; border-radius: 18px; background: #fbfcfd; }
.seo-card h3 { margin: 0 0 10px; color: #202a34; font-size: 18px; }
.seo-card p { margin: 0; color: #707d87; line-height: 1.7; }
.how-to-section .steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.how-to-section .step { padding: 26px 22px; border: 1px solid #e5ebee; border-radius: 17px; background: #fff; }
.faq-list { max-width: 900px; margin: 0 auto; }
.faq-item { margin-bottom: 10px; padding: 0 22px; border: 1px solid #e7edf1; border-radius: 15px; background: #fbfcfd; }
.faq-item summary { position: relative; cursor: pointer; padding: 20px 38px 20px 0; font-weight: 750; list-style: none; }
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after { content: '+'; position: absolute; top: 15px; right: 0; width: 30px; height: 30px; border-radius: 50%; background: #e8f8f3; color: #07856f; font-size: 20px; line-height: 28px; text-align: center; }
.faq-item[open] summary::after { content: '−'; }
.faq-item p { margin: 0; padding: 0 38px 20px 0; color: #697681; line-height: 1.7; }
.image-lightbox { position: fixed; z-index: 3000; inset: 0; display: flex; align-items: center; justify-content: center; padding: 48px; background: rgba(7, 12, 16, .88); cursor: zoom-out; }
.image-lightbox img { max-width: 94vw; max-height: 90vh; object-fit: contain; border-radius: 10px; }
.lightbox-close { position: absolute; top: 20px; right: 24px; border: 0; background: transparent; color: #fff; font-size: 38px; cursor: pointer; }
@media (max-width: 1100px) {
  .first-screen-grid { grid-template-columns: 1fr; max-width: 760px; }
  .first-screen { min-height: auto; }
  .feature-row { gap: 40px; }
}
@media (max-width: 768px) {
  .first-screen { width: 100vw; margin-left: calc(50% - 50vw); padding: 18px 16px 28px; }
  .first-screen .tool-hero-info h1 { font-size: 34px; }
  .first-screen .tool-interface, .first-screen .login-prompt, .workspace-panel { min-height: auto; }
  .workspace-panel .demo-comparison { min-height: 0; flex-direction: column; }
  .workspace-panel .demo-item { width: 100%; max-width: none; }
  .workspace-panel .demo-image { aspect-ratio: 4 / 3; }
  .workspace-panel .demo-arrow { transform: rotate(90deg); }
  .processing-preview { height: 320px; }
  .workspace-panel .output-comparison { grid-template-columns: 1fr; }
  .workspace-panel .output-image { min-height: 260px; }
  .seo-content-start { margin-top: 68px; }
  .detail-section { margin-bottom: 68px; }
  .section-heading h2 { font-size: 28px; }
  .feature-row { grid-template-columns: 1fr; gap: 24px; }
  .feature-row:nth-child(even) .feature-visual, .feature-row:nth-child(even) .feature-copy { order: initial; }
  .feature-visual { min-height: 300px; }
  .seo-card-grid, .how-to-section .steps { grid-template-columns: 1fr; }
  .image-lightbox { padding: 20px; }
}

/* Pollo 参考页排版：首屏独立，SEO 内容从下一屏开始。 */
.pollo-layout { padding-bottom: 64px; }
.pollo-layout .first-screen { position: relative; width: calc(100vw - 48px); min-height: calc(100vh - 24px); margin-left: calc(50% - 50vw + 24px); padding: 26px 32px 30px; }
.pollo-layout .first-screen .breadcrumb { position: absolute; z-index: 3; top: 42px; left: 48px; width: 410px; margin: 0; }
.pollo-layout .first-screen .tool-hero { width: 100%; margin: 0; }
.pollo-layout .first-screen .tool-hero-info { max-width: 760px; margin: 0 auto; text-align: center; }
.pollo-layout .first-screen .tool-hero-info h1 { font-size: 36px; line-height: 1.32; }
.pollo-layout .first-screen .tool-icon-lg { display: none; }
.pollo-layout .first-screen-grid { grid-template-columns: 468px minmax(0, 1fr); gap: 72px; max-width: 1392px; min-height: calc(100vh - 84px); }
.pollo-layout .first-screen .showcase-column { display: flex; min-width: 0; flex-direction: column; justify-content: center; gap: 28px; }
.pollo-layout .first-screen .action-column { padding-top: 54px; }
.pollo-layout .first-screen .tool-interface,
.pollo-layout .first-screen .login-prompt { min-height: 0; padding: 82px 20px 20px; border-radius: 16px; }
.pollo-layout .first-screen .showcase-column > .workspace-panel { width: min(710px, 100%); min-height: 0; margin: 0 auto; }
.pollo-layout .first-screen .workspace-panel .demo-comparison { height: 406px; }
.pollo-layout .first-screen .workspace-panel .demo-image { height: 100%; aspect-ratio: auto; }
.pollo-layout .first-screen .processing-preview { height: 390px; }
.pollo-layout .seo-content-start { margin-top: 0; padding-top: 112px; }
.pollo-layout .detail-section { margin-bottom: 112px; }
.pollo-layout .section-heading { max-width: 800px; margin-bottom: 30px; }
.pollo-layout .section-heading h2,
.pollo-layout .detail-section > h2 { font-size: 40px; line-height: 1.4; }
.pollo-layout .section-heading p { font-size: 16px; }
.section-primary-cta { display: table; min-width: 190px; margin: 0 auto 48px; padding: 13px 28px; border: 1px solid #0a9c82; border-radius: 999px; color: #087d68; font-size: 15px; font-weight: 750; text-align: center; }
.section-primary-cta:hover { color: #fff; background: #0a9c82; }
.landing-demo-comparison { position: relative; display: grid; grid-template-columns: 1fr 1fr; width: min(880px, 100%); height: 500px; margin: 0 auto 112px; overflow: hidden; border-radius: 18px; background: #edf1f3; box-shadow: 0 20px 55px rgba(31, 50, 63, .13); }
.landing-demo-half { min-width: 0; overflow: hidden; }
.landing-demo-half img { width: 100%; height: 100%; object-fit: cover; }
.landing-demo-divider { position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; background: rgba(255, 255, 255, .94); transform: translateX(-1px); }
.pollo-layout .feature-list { gap: 96px; }
.pollo-layout .feature-row { grid-template-columns: minmax(0, 480px) minmax(320px, 480px); justify-content: space-between; gap: 72px; }
.pollo-layout .feature-visual { min-height: 0; height: 320px; border-radius: 16px; }
.pollo-layout .feature-copy h3 { font-size: 40px; line-height: 1.4; }
.pollo-layout .feature-copy p { font-size: 16px; }
.pollo-layout .how-to-section .steps { gap: 24px; }
.pollo-layout .how-to-section .step { min-height: 142px; padding: 30px; text-align: center; }
.pollo-layout .how-to-section .step-number { margin-right: auto; margin-left: auto; }
.pollo-layout .more-tools-section > h2 { margin-bottom: 44px; text-align: center; }
.pollo-layout .more-tools-section .grid-5 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.pollo-layout .more-tools-section .grid-5 > :nth-child(n + 5) { display: none; }
.pollo-layout .faq-section { max-width: 960px; margin-right: auto; margin-bottom: 48px; margin-left: auto; padding: 74px 36px; }
.pollo-layout .faq-section .section-heading { margin-right: auto; margin-left: auto; text-align: center; }
.pollo-layout .tool-bottom-how-to { margin-top: 0; }
.pollo-layout .faq-item { margin: 0; padding: 0 8px; border-width: 0 0 1px; border-radius: 0; background: transparent; }
.pollo-layout .faq-item summary { padding: 23px 42px 23px 28px; }
.pollo-layout .faq-item summary::after { right: auto; left: 0; background: transparent; color: #53606b; }
.pollo-layout .faq-item p { padding: 0 42px 22px 28px; }

@media (max-width: 1100px) {
  .pollo-layout .first-screen { min-height: auto; }
  .pollo-layout .first-screen .breadcrumb,
  .pollo-layout .first-screen .tool-hero { position: static; width: auto; margin: 0 auto 24px; }
  .pollo-layout .first-screen-grid { grid-template-columns: 1fr; max-width: 760px; min-height: 0; }
  .pollo-layout .first-screen .action-column { padding-top: 0; }
  .pollo-layout .first-screen .tool-interface,
  .pollo-layout .first-screen .login-prompt { padding-top: 24px; }
  .pollo-layout .first-screen .showcase-column > .workspace-panel { margin-top: 0; }
}

@media (max-width: 768px) {
  .pollo-layout { padding-bottom: 32px; }
  .pollo-layout .first-screen { width: 100vw; margin-left: calc(50% - 50vw); padding: 20px 16px 30px; }
  .pollo-layout .first-screen .showcase-column { display: none; }
  .pollo-layout .first-screen .breadcrumb { margin-bottom: 16px; }
  .pollo-layout .first-screen .tool-interface,
  .pollo-layout .first-screen .login-prompt { min-height: 530px; padding: 20px 12px; }
  .pollo-layout .seo-content-start { padding-top: 76px; }
  .pollo-layout .detail-section { margin-bottom: 76px; }
  .pollo-layout .section-heading h2,
  .pollo-layout .detail-section > h2 { font-size: 28px; }
  .section-primary-cta { margin-bottom: 34px; }
  .landing-demo-comparison { height: 320px; margin-bottom: 72px; border-radius: 12px; }
  .pollo-layout .feature-list { gap: 68px; }
  .pollo-layout .feature-row { gap: 22px; text-align: center; }
  .pollo-layout .feature-visual,
  .pollo-layout .feature-row:nth-child(even) .feature-visual { height: 320px; order: 1; }
  .pollo-layout .feature-copy,
  .pollo-layout .feature-row:nth-child(even) .feature-copy { max-width: none; order: 2; }
  .pollo-layout .feature-copy h3 { font-size: 24px; }
  .pollo-layout .more-tools-section .grid-5 { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .pollo-layout .faq-section { margin-bottom: 32px; padding: 48px 0; }
}
</style>
